// documents#188 — per-product AI labelling status, as the product lists carry
// it (GET /brands/:id/products and the embedded /shopify/admin/products):
//
//   ai_labeling: {
//     status:       'PENDING' | 'DONE' | 'FAILED' | 'SKIPPED' | null,
//     requested_at: ISO | null,
//     completed_at: ISO | null,
//     attempts:     number,
//     category:     string | null   // clothing category once labelled
//   }
//
// null status = the row was written by a sync from before the queue existed
// and was never queued — no badge. Pure helpers, no React: the badge and the
// Products tab's poll loop are built on these, and they are unit-tested here
// without the tab's other dependencies.

export const LABELING_STATUS = Object.freeze({
  PENDING: 'PENDING',
  DONE: 'DONE',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
});

/** How often the Products tab re-reads the list while a label is PENDING. */
export const LABELING_POLL_MS = 5000;

/** Normalised status of one product's ai_labeling block, or null (no badge). */
export function labelingStatus(product) {
  const raw = product?.ai_labeling?.status;
  if (typeof raw !== 'string') return null;
  const up = raw.trim().toUpperCase();
  return Object.values(LABELING_STATUS).includes(up) ? up : null;
}

/**
 * What the badge renders for one product.
 *
 * @returns {null | {
 *   status: string,
 *   variant: 'pending'|'done'|'failed'|'skipped',
 *   key: string,        // i18n key
 *   vars: Object,       // {category} for DONE with a category
 *   retry: boolean      // FAILED only — offer the "Retry" button
 * }}
 */
export function describeLabeling(product) {
  const status = labelingStatus(product);
  if (!status) return null;

  if (status === LABELING_STATUS.PENDING) {
    return { status, variant: 'pending', key: 'products.labelPending', vars: {}, retry: false };
  }
  if (status === LABELING_STATUS.DONE) {
    const category = product?.ai_labeling?.category;
    const has = typeof category === 'string' && category.trim() !== '';
    return {
      status,
      variant: 'done',
      key: has ? 'products.labelDoneCategory' : 'products.labelDone',
      vars: has ? { category: category.trim() } : {},
      retry: false,
    };
  }
  if (status === LABELING_STATUS.FAILED) {
    return { status, variant: 'failed', key: 'products.labelFailed', vars: {}, retry: true };
  }
  return { status, variant: 'skipped', key: 'products.labelSkipped', vars: {}, retry: false };
}

/** True while at least one product in the list is still being labelled. */
export function hasPendingLabeling(products) {
  if (!Array.isArray(products)) return false;
  return products.some((p) => labelingStatus(p) === LABELING_STATUS.PENDING);
}

/**
 * The list with one product's ai_labeling replaced — what the tab does with a
 * relabel's 202 body (`{ product_id, ai_labeling }`) so the card flips to
 * PENDING at once, before the next poll confirms it.
 */
export function labelingFinished(product) {
  return labelingStatus(product) !== LABELING_STATUS.PENDING;
}

function disputeTime(dispute) {
  const ms = Date.parse(dispute?.created_at);
  return Number.isNaN(ms) ? -Infinity : ms;
}

export function latestDisputesByProduct(disputes) {
  const byProduct = {};
  if (!Array.isArray(disputes)) return byProduct;
  for (const d of disputes) {
    if (!d || d.product_id == null) continue;
    const key = String(d.product_id);
    const prev = byProduct[key];
    if (!prev || disputeTime(d) > disputeTime(prev)) {
      byProduct[key] = d;
    }
  }
  return byProduct;
}

export function withLabeling(products, productId, aiLabeling) {
  if (!Array.isArray(products)) return products;
  return products.map((p) => (
    String(p?.id) === String(productId) ? { ...p, ai_labeling: aiLabeling } : p
  ));
}
